import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'

/**
 * @fileoverview eNPS Detail Store
 *
 * Zustand store for managing eNPS configuration state and mutations
 */

interface EnpsFormData {
	name: string
	description: string | null
	cadence: 'one-time' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'annually'
	populationId: string | null
	emailTemplateId: string | null
	questionText: string
	followUpQuestionText: string
	textFeedbackEnabled: boolean
	nextScheduledDate: Date | null
	allowExternalParticipants: boolean
}

export interface EnpsData {
	id: string
	name: string
	description: string | null
	status: 'draft' | 'active' | 'paused' | 'archived'
	organizationId: string
	cadence: 'one-time' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'annually'
	populationId: string | null
	emailTemplateId: string | null
	questionText: string
	followUpQuestionText: string
	textFeedbackEnabled: boolean
	allowExternalParticipants: boolean
	nextScheduledDate: Date | null
	lastExecutionDate: Date | null
	createdAt: Date
	updatedAt: Date
}

interface EnpsState {
	enpsConfig: EnpsData | null
	isLoading: boolean
	error: string | null

	setEnpsConfig: (config: EnpsData) => void
	resetEnpsConfig: () => void
	saveEnpsConfig: () => EnpsFormData
	updateEnpsConfig: (data: Partial<EnpsFormData>) => void
	setError: (error: string | null) => void

	useCreateEnpsConfig: () => {
		createEnpsConfig: (data: EnpsFormData) => Promise<void>
		isLoading: boolean
	}
	useEditEnpsConfig: (enpsId?: string) => {
		editEnpsConfig: (data: Partial<EnpsFormData>) => Promise<void>
		isLoading: boolean
	}
	useActivateEnpsConfig: (enpsId: string) => {
		activateEnpsConfig: () => Promise<void>
		isLoading: boolean
	}
	usePauseEnpsConfig: (enpsId: string) => {
		pauseEnpsConfig: () => Promise<void>
		isLoading: boolean
	}
	useArchiveEnpsConfig: (enpsId: string) => {
		archiveEnpsConfig: () => Promise<void>
		isLoading: boolean
	}
}

export const useEnpsStore = create<EnpsState>()(
	devtools(
		immer((set, get) => ({
			enpsConfig: null,
			isLoading: false,
			error: null,

			setEnpsConfig: (config: EnpsData) =>
				set((state) => {
					state.enpsConfig = config
				}),

			resetEnpsConfig: () =>
				set((state) => {
					state.enpsConfig = null
					state.error = null
				}),

			updateEnpsConfig: (data: Partial<EnpsFormData>) =>
				set((state) => {
					if (state.enpsConfig) {
						state.enpsConfig = {
							...state.enpsConfig,
							...data,
						}
					} else {
						state.enpsConfig = {
							id: '',
							name: '',
							description: null,
							status: 'draft',
							organizationId: '',
							cadence: 'quarterly',
							populationId: null,
							emailTemplateId: null,
							questionText: 'How likely are you to recommend our organization as a place to work?',
							followUpQuestionText: 'What is the primary reason for your score?',
							textFeedbackEnabled: true,
							allowExternalParticipants: false,
							nextScheduledDate: null,
							lastExecutionDate: null,
							createdAt: new Date(),
							updatedAt: new Date(),
							...data,
						}
					}
				}),

			setError: (error: string | null) =>
				set((state) => {
					state.error = error
				}),

			saveEnpsConfig: () => {
				const state = get()
				if (!state.enpsConfig) throw new Error('No NPS config data to save')
				return {
					name: state.enpsConfig.name,
					description: state.enpsConfig.description,
					cadence: state.enpsConfig.cadence,
					populationId: state.enpsConfig.populationId,
					emailTemplateId: state.enpsConfig.emailTemplateId,
					questionText: state.enpsConfig.questionText,
					followUpQuestionText: state.enpsConfig.followUpQuestionText,
					textFeedbackEnabled: state.enpsConfig.textFeedbackEnabled,
					nextScheduledDate: state.enpsConfig.nextScheduledDate,
					allowExternalParticipants: state.enpsConfig.allowExternalParticipants,
				}
			},

			useCreateEnpsConfig: () => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const navigate = useNavigate()

				const createMutation = useMutation({
					...trpc.client.enps.create.mutationOptions(),
					onSuccess: (data) => {
						toast.showToast('NPS configuration created successfully')
						navigate(`/enps/${data.id}/general`)
					},
					onError: () => {
						toast.showToast('Error creating NPS configuration', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({ queryKey: [['enps']] })
					},
				})

				return {
					createEnpsConfig: async (data: EnpsFormData) => {
						const current = get().enpsConfig
						if (!current?.organizationId) {
							throw new Error('Organization data is missing. Please reload the page.')
						}
						await createMutation.mutateAsync({
							organizationId: current.organizationId,
							name: data.name,
							description: data.description ?? undefined,
							cadence: data.cadence,
							populationId: data.populationId ?? undefined,
							emailTemplateId: data.emailTemplateId ?? undefined,
							questionText: data.questionText,
							followUpQuestionText: data.followUpQuestionText,
						})
					},
					isLoading: createMutation.isPending,
				}
			},

			useEditEnpsConfig: (enpsId?: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const editMutation = useMutation({
					...trpc.client.enps.update.mutationOptions(),
					onSuccess: () => {
						toast.showToast('NPS configuration updated successfully')
					},
					onError: () => {
						toast.showToast('Error updating NPS configuration', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getById.queryKey({ id: enpsId }),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					editEnpsConfig: async (data: Partial<EnpsFormData>) => {
						const current = get().enpsConfig
						if (!current?.id || !current?.organizationId) {
							throw new Error('Configuration data is missing. Please reload the page.')
						}
						await editMutation.mutateAsync({
							id: current.id,
							organizationId: current.organizationId,
							name: data.name,
							description: data.description ?? undefined,
							cadence: data.cadence,
							populationId: data.populationId,
							emailTemplateId: data.emailTemplateId,
							questionText: data.questionText,
							followUpQuestionText: data.followUpQuestionText,
							textFeedbackEnabled: data.textFeedbackEnabled,
							allowExternalParticipants: data.allowExternalParticipants,
							nextScheduledDate: data.nextScheduledDate,
						})
					},
					isLoading: editMutation.isPending,
				}
			},

			useActivateEnpsConfig: (enpsId: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const activateMutation = useMutation({
					...trpc.client.enps.activate.mutationOptions(),
					onSuccess: () => {
						toast.showToast('NPS configuration activated')
					},
					onError: () => {
						toast.showToast('Error activating NPS configuration', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getById.queryKey({ id: enpsId }),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getConfigResults.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getActiveExecution.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getExecutionById.queryKey(),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					activateEnpsConfig: async () => {
						const current = get().enpsConfig
						if (!current?.organizationId) {
							throw new Error('Organization data is missing. Please reload the page.')
						}
						await activateMutation.mutateAsync({
							id: enpsId,
							organizationId: current.organizationId,
						})
					},
					isLoading: activateMutation.isPending,
				}
			},

			usePauseEnpsConfig: (enpsId: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const pauseMutation = useMutation({
					...trpc.client.enps.pause.mutationOptions(),
					onSuccess: () => {
						toast.showToast('NPS configuration paused')
					},
					onError: () => {
						toast.showToast('Error pausing NPS configuration', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getById.queryKey({ id: enpsId }),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getConfigResults.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getActiveExecution.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getExecutionById.queryKey(),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					pauseEnpsConfig: async () => {
						const current = get().enpsConfig
						if (!current?.organizationId) {
							throw new Error('Organization data is missing. Please reload the page.')
						}
						await pauseMutation.mutateAsync({
							id: enpsId,
							organizationId: current.organizationId,
						})
					},
					isLoading: pauseMutation.isPending,
				}
			},

			useArchiveEnpsConfig: (enpsId: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()

				const archiveMutation = useMutation({
					...trpc.client.enps.archive.mutationOptions(),
					onSuccess: () => {
						toast.showToast('NPS configuration archived')
					},
					onError: () => {
						toast.showToast('Error archiving NPS configuration', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getById.queryKey({ id: enpsId }),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getConfigResults.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getActiveExecution.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.client.enps.getExecutionById.queryKey(),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					archiveEnpsConfig: async () => {
						const current = get().enpsConfig
						if (!current?.organizationId) {
							throw new Error('Organization data is missing. Please reload the page.')
						}
						await archiveMutation.mutateAsync({
							id: enpsId,
							organizationId: current.organizationId,
						})
					},
					isLoading: archiveMutation.isPending,
				}
			},
		})),
		{ name: 'enps-store' },
	),
)
